var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { objectPosition } from "3d/constants/common";
/*
 * One layer of cardboard boxes saved on its own, outside any one system, so it
 * can be laid out again — on either layer — in other systems: a saved preset
 * of the user's own, next to the built-in ones (a user library item of kind
 * `palletLayoutKind`, see `store/user/reducer`).
 *
 * The pallet and box size belong to the system, not to a layer: both layers
 * share them, so a saved layer laid out on one of them must not change them.
 * The layer only says where its boxes stand and how they are turned, centred
 * on whatever pallet the system has. The sizes it was arranged for are kept so
 * the UI can say when they differ from the system's (`hasSizeMismatch`).
 *
 * The backend stores this as opaque JSON, so the format is owned — and
 * versioned — here, independently of the scene's persist version: a saved
 * layout outlives the system it came from. To change it, bump
 * `currentPalletLayoutVersion`, add the upgrade from the previous version to
 * `migrations`, and extend the parser. New per-box properties are best added
 * as optional fields that fall back to the shared value, which needs no
 * migration at all — the way a box's own `size` falls back to the system's.
 */
export var palletLayoutKind = "palletLayout";
export var currentPalletLayoutVersion = 2;
/*
 * Upgrades from the previous version, keyed by the version they produce. Each
 * takes the raw (unvalidated) data of the version before it.
 */
var migrations = {
    /* Version 1 saved the whole pallet, both layers. Its first layer — the
     * one a pallet always has — becomes the saved layer. */
    2: function (_a) {
        var pallet = _a.pallet, boxSize = _a.boxSize, boxes = _a.boxes;
        return ({
            version: 2,
            pallet: pallet,
            boxSize: boxSize,
            boxes: Array.isArray(boxes)
                ? boxes
                    .filter(function (box) { return isRecord(box) && box.layer === 0; })
                    .map(function (_a) {
                    var _layer = _a.layer, box = __rest(_a, ["layer"]);
                    return box;
                })
                : boxes,
        });
    },
};
/* The given layer of the scene, as it would be saved. */
export var createPalletLayout = function (scene, layer) { return ({
    version: currentPalletLayoutVersion,
    pallet: __assign({}, scene.pallet),
    boxSize: __assign({}, scene.objectSize),
    boxes: scene.cardboardBoxes
        .filter(function (box) { return box.layer === layer; })
        .map(function (box) { return (__assign({ position: {
            x: box.position.x - objectPosition.x,
            z: box.position.z - objectPosition.z,
        }, rotation: box.rotation }, (box.size && { size: __assign({}, box.size) }))); }),
}); };
/*
 * The boxes a layout lays out on the given layer, back in world coordinates.
 *
 * Box ids follow the ones `stampLayer` gives a pattern's boxes (layer and index
 * within the layer), so the cup-to-box assignments and per-box forces keyed by
 * them keep pointing at the box in the same place where they can.
 */
export var palletLayoutToBoxes = function (layout, layer) {
    return layout.boxes.map(function (box, index) { return (__assign({ id: "cardboard-box-l".concat(layer, "-").concat(index), position: {
            x: box.position.x + objectPosition.x,
            z: box.position.z + objectPosition.z,
        }, rotation: box.rotation, layer: layer }, (box.size && { size: __assign({}, box.size) }))); });
};
/* Small enough to absorb unit round-trips, far below anything a user types. */
var SIZE_TOLERANCE = 1e-6;
var isSameLength = function (a, b) { return Math.abs(a - b) < SIZE_TOLERANCE; };
/*
 * Whether a layout was arranged for another pallet or box footprint than the
 * system has — laid out here, its boxes may overlap, leave gaps or overhang.
 * The box height plays no part in where boxes stand, so it is not compared.
 */
export var hasSizeMismatch = function (layout, scene) {
    return !isSameLength(layout.pallet.length, scene.pallet.length) ||
        !isSameLength(layout.pallet.width, scene.pallet.width) ||
        !isSameLength(layout.boxSize.x, scene.objectSize.x) ||
        !isSameLength(layout.boxSize.z, scene.objectSize.z);
};
var isRecord = function (value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};
var isFiniteNumber = function (value) {
    return typeof value === "number" && Number.isFinite(value);
};
var isPositiveNumber = function (value) {
    return isFiniteNumber(value) && value > 0;
};
var parseBox = function (value) {
    if (!isRecord(value) || !isRecord(value.position))
        return undefined;
    var position = value.position, rotation = value.rotation;
    if (!isFiniteNumber(position.x) || !isFiniteNumber(position.z)) {
        return undefined;
    }
    if (!isFiniteNumber(rotation) || rotation % 90 !== 0)
        return undefined;
    /* Optional: a box without one has the system's size. A size that is there
     * must be a real one, though — dropping it would silently resize the box. */
    var size = value.size;
    var hasValidSize = isRecord(size) && isPositiveNumber(size.x) && isPositiveNumber(size.z);
    if (size !== undefined && !hasValidSize)
        return undefined;
    return __assign({ position: { x: position.x, z: position.z }, rotation: ((rotation % 360) + 360) % 360 }, (hasValidSize && { size: { x: size.x, z: size.z } }));
};
/*
 * Reads a saved layout back, upgrading older versions first. Anything that
 * doesn't hold up — a layout saved by a newer client, or data that isn't a
 * layout at all — yields `undefined`, so it's skipped rather than laid out
 * half-broken.
 */
export var parsePalletLayout = function (data) {
    if (!isRecord(data) || !Number.isInteger(data.version))
        return undefined;
    var upgraded = data;
    for (var version = data.version + 1; version <= currentPalletLayoutVersion; version++) {
        var migrate = migrations[version];
        if (!migrate || !isRecord(upgraded))
            return undefined;
        upgraded = migrate(upgraded);
    }
    if (!isRecord(upgraded) || upgraded.version !== currentPalletLayoutVersion) {
        return undefined;
    }
    var pallet = upgraded.pallet, boxSize = upgraded.boxSize, boxes = upgraded.boxes;
    if (!isRecord(pallet) ||
        !isPositiveNumber(pallet.length) ||
        !isPositiveNumber(pallet.width)) {
        return undefined;
    }
    if (!isRecord(boxSize) ||
        !isPositiveNumber(boxSize.x) ||
        !isPositiveNumber(boxSize.y) ||
        !isPositiveNumber(boxSize.z)) {
        return undefined;
    }
    if (!Array.isArray(boxes))
        return undefined;
    var parsedBoxes = boxes.map(parseBox);
    if (parsedBoxes.some(function (box) { return box === undefined; }))
        return undefined;
    // A layer in use always holds a box; a layout without one has nothing to show.
    if (parsedBoxes.length === 0)
        return undefined;
    return {
        version: currentPalletLayoutVersion,
        pallet: { length: pallet.length, width: pallet.width },
        boxSize: { x: boxSize.x, y: boxSize.y, z: boxSize.z },
        boxes: parsedBoxes,
    };
};
